import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationalRoleService, CreateOrganizationalRoleDto, UpdateOrganizationalRoleDto } from '../services/organizationalRoleService';
import { OrganizationalRole } from '../types';

export const useOrganizationalRoles = (includeInactive = false) => {
  return useQuery<OrganizationalRole[]>({
    queryKey: ['organizational-roles', includeInactive],
    queryFn: () => organizationalRoleService.getAll(includeInactive),
  });
};

export const useOrganizationalRole = (id: string) => {
  return useQuery<OrganizationalRole>({
    queryKey: ['organizational-role', id],
    queryFn: () => organizationalRoleService.getOne(id),
    enabled: !!id,
  });
};

export const useCreateOrganizationalRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationalRoleDto) => organizationalRoleService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles'] });
    },
  });
};

export const useUpdateOrganizationalRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationalRoleDto }) =>
      organizationalRoleService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles'] });
      queryClient.invalidateQueries({ queryKey: ['organizational-role'] });
    },
  });
};

export const useDeleteOrganizationalRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organizationalRoleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles'] });
    },
  });
};

export const useSeedOrganizationalRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => organizationalRoleService.seed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles'] });
    },
  });
};
